import { memo } from 'react';
import MathText from './MathText';
import { pluriel, texteLisible } from './formatConfig';
import normalize from '../../utils/normalize';
import { espacerMilliersTexte } from '../../utils/milliers';

/**
 * Surligne `terme` (déjà normalisé) dans `texte`, accents ignorés : la
 * recherche se fait sur le texte normalisé caractère par caractère, la marque
 * s'applique au texte d'origine. Un terme pris dans une formule `$…$` n'est
 * pas surligné (la formule est rendue d'un bloc).
 */
const marquer = (texte, terme) => {
  if (!terme) return texte;
  let norme = '';
  const origine = [];
  for (let i = 0; i < texte.length; i++) {
    for (const c of normalize(texte[i])) { norme += c; origine.push(i); }
  }
  const morceaux = [];
  let dernier = 0;
  for (let k = norme.indexOf(terme); k !== -1; k = norme.indexOf(terme, k + terme.length)) {
    const debut = origine[k];
    const fin = origine[k + terme.length - 1] + 1;
    if (debut > dernier) morceaux.push(texte.slice(dernier, debut));
    morceaux.push(<mark key={k} className="rounded-sm px-[1px] text-inherit" style={{ background: 'rgba(250,204,21,0.28)' }}>{texte.slice(debut, fin)}</mark>);
    dernier = fin;
  }
  if (dernier < texte.length) morceaux.push(texte.slice(dernier));
  return morceaux;
};

/** Libellé d'attendu : formules `$…$` rendues, terme surligné dans le texte autour. */
export const Libelle = ({ texte, terme }) => (
  <>
    {texte.split(/(\$[^$]+\$)/g).map((morceau, i) => (morceau.length > 1 && morceau.startsWith('$') && morceau.endsWith('$')
      ? <MathText key={i}>{morceau}</MathText>
      : <span key={i}>{marquer(espacerMilliersTexte(morceau), terme)}</span>))}
  </>
);

// Le fil de l'arbre des prérequis (13/10/2026) : cyan, 2 px, centré à 14 px
// dans une colonne de 28 px ; le point de l'attendu en bleu TanoEd.
const TRAIT = 'absolute left-[13px] border-cyan-600';

/**
 * Une colonne du fil (cf. `filsDeLArbre`) : `tronc` (le point de l'attendu et
 * le départ du fil), `point` (le point seul, prérequis repliés), `branche`
 * (├), `fin` (└), `passe` (│) ou `vide`.
 */
export const ColonneFil = ({ morceau }) => (
  <span aria-hidden="true" className="relative w-7 shrink-0">
    {morceau === 'passe' || morceau === 'branche' ? <span className={`${TRAIT} inset-y-0 border-l-2`} /> : null}
    {morceau === 'fin' ? <span className={`${TRAIT} top-0 h-1/2 border-l-2`} /> : null}
    {morceau === 'tronc' ? <span className={`${TRAIT} top-1/2 bottom-0 border-l-2`} /> : null}
    {morceau === 'branche' || morceau === 'fin' ? <span className={`${TRAIT} top-1/2 w-[15px] border-t-2`} /> : null}
    {morceau === 'tronc' || morceau === 'point' ? <span className="absolute left-2 top-1/2 -mt-1.5 w-3 h-3 rounded-full bg-blue-900" /> : null}
  </span>
);

/**
 * Une ligne d'attendu de la popup « Que voulez-vous travailler ? » (dessin du
 * 13/10/2026) : libellé (formules rendues, terme surligné), « en cours » sur
 * l'attendu choisi, niveau (dans les arbres seulement), nombre d'exercices.
 * - `fils` : les colonnes du fil d'une ligne d'arbre (carte ou recherche) ;
 *   `null` dans la navigation par thème, qui n'en a pas.
 * - `etat` : 'en-cours' pour l'attendu en tête de la carte (son titre le dit
 *   déjà : pas de pastille « en cours »).
 * - `children` : rendu sous la ligne, dans le même élément de liste.
 */
export const LigneAttendu = memo(({ att, niveau, profondeur = 0, fils = null, terme = '', etat = null, actif = false, avecNiveau = true, onChoisir, modeRevue = false, children = null }) => {
  const racine = fils !== null && profondeur === 0;
  const enCours = actif && etat !== 'en-cours';
  let libelle = 'font-medium text-slate-900';
  if (enCours) libelle = 'font-bold text-blue-900';
  else if (racine) libelle = 'font-bold text-slate-900';
  return (
    <li>
      <button
        type="button"
        onClick={() => onChoisir(att)}
        aria-current={actif ? 'true' : undefined}
        aria-label={`${texteLisible(att.description || '')}, ${niveau}e${att.totalExos > 0 ? `, ${pluriel(att.totalExos, 'exercice')}` : ''}${profondeur > 0 ? ', prérequis' : ''}${etat === 'en-cours' ? ', en cours' : ''}`}
        className={`w-full text-left flex items-stretch min-h-[48px] pr-2 rounded-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500 ${fils ? '' : 'pl-2'} ${enCours ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'}`}
      >
        {fils ? fils.map((morceau, i) => <ColonneFil key={i} morceau={morceau} />) : null}
        <span className={`flex items-center gap-3 flex-1 min-w-0 py-1.5 ${fils ? 'pl-2' : ''}`}>
          {modeRevue ? (
            <span className="shrink-0 font-mono text-[11px] font-bold px-1.5 py-[1px] rounded-md bg-slate-100 text-slate-700">{att.id}</span>
          ) : null}
          <span className={`flex-1 min-w-0 text-base leading-snug ${libelle}`}>
            <Libelle texte={att.description || ''} terme={terme} />
          </span>
          {enCours ? <span className="shrink-0 text-xs font-bold text-white bg-blue-900 px-2.5 py-0.5 rounded-full">en cours</span> : null}
          {avecNiveau ? <span className="shrink-0 w-9 text-center text-[13px] font-bold text-slate-700 bg-slate-100 rounded-md py-0.5">{niveau}e</span> : null}
          <span className="shrink-0 w-24 text-right text-[13px] font-semibold text-slate-500 tabular-nums">
            {att.totalExos > 0 ? pluriel(att.totalExos, 'exercice') : ''}
          </span>
        </span>
      </button>
      {children}
    </li>
  );
});
LigneAttendu.displayName = 'LigneAttendu';
